import { useEffect, useState } from "react";
import { AdminSchoolsAPI } from "../../api/adminSchools";

export default function AdminSchoolsPage() {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [newSchoolName, setNewSchoolName] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [loading, setLoading] = useState(true);

  const [arborBaseUrl, setArborBaseUrl] = useState("");
  const [arborEnabled, setArborEnabled] = useState(true);
  const [arborSaving, setArborSaving] = useState(false);
  const [arborMessage, setArborMessage] = useState("");

  async function load() {
    setLoading(true);

    const data = await AdminSchoolsAPI.getAll();
    setSchools(data);

    if (selectedSchool?.id) {
      const refreshed = data.find((s) => s.id === selectedSchool.id);
      setSelectedSchool(refreshed || null);
    }

    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!selectedSchool) {
      setArborBaseUrl("");
      setArborEnabled(true);
      setArborMessage("");
      return;
    }

    setArborBaseUrl(
      selectedSchool.arbor_integration?.base_url || ""
    );

    setArborEnabled(
      selectedSchool.arbor_integration?.enabled ?? true
    );

    setArborMessage("");
  }, [selectedSchool?.id, selectedSchool?.arbor_integration]);

  async function handleCreateSchool() {
    if (!newSchoolName.trim()) return;

    await AdminSchoolsAPI.create({
      name: newSchoolName,
    });

    setNewSchoolName("");
    await load();
  }

  async function handleUpdateSchool() {
    if (!selectedSchool) return;

    await AdminSchoolsAPI.update(selectedSchool.id, {
      name: selectedSchool.name,
    });

    await load();
  }

  async function handleAddDomain() {
    if (!selectedSchool || !newDomain.trim()) return;

    await AdminSchoolsAPI.addDomain(selectedSchool.id, {
      domain: newDomain,
      is_primary: false,
      is_active: true,
    });

    setNewDomain("");
    await load();
  }

  async function handleDeleteDomain(domainId) {
    await AdminSchoolsAPI.deleteDomain(domainId);
    await load();
  }

  async function togglePrimary(domain) {
    await AdminSchoolsAPI.updateDomain(domain.id, {
      domain: domain.domain,
      is_primary: true,
      is_active: domain.is_active,
    });

    await load();
  }

  async function toggleActive(domain) {
    await AdminSchoolsAPI.updateDomain(domain.id, {
      domain: domain.domain,
      is_primary: domain.is_primary,
      is_active: !domain.is_active,
    });

    await load();
  }

  async function handleSaveArborIntegration() {
    if (!selectedSchool || !arborBaseUrl.trim()) return;

    try {
      setArborSaving(true);
      setArborMessage("");

      await AdminSchoolsAPI.saveArborIntegration(
        selectedSchool.id,
        {
          base_url: arborBaseUrl,
          enabled: arborEnabled,
        }
      );

      await load();

      setArborMessage("Arbor integration saved.");
    } catch (err) {
      setArborMessage(
        err.message || "Failed to save Arbor integration."
      );
    } finally {
      setArborSaving(false);
    }
  }

  async function handleDeleteArborIntegration() {
    if (!selectedSchool?.arbor_integration) return;

    const confirmed = window.confirm(
      `Remove the Arbor integration for ${selectedSchool.name}?`
    );

    if (!confirmed) return;

    try {
      setArborSaving(true);
      setArborMessage("");

      await AdminSchoolsAPI.deleteArborIntegration(
        selectedSchool.id
      );

      await load();

      setArborMessage("Arbor integration removed.");
    } catch (err) {
      setArborMessage(
        err.message || "Failed to remove Arbor integration."
      );
    } finally {
      setArborSaving(false);
    }
  }

  if (loading) return <div>Loading...</div>;

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <h1 className="text-2xl font-semibold">Schools</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">

        {/* LEFT: SCHOOL LIST */}
        <div className="border rounded p-4 bg-white space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Schools
            </label>

            <div className="border rounded divide-y max-h-[320px] overflow-y-auto">
              {schools.map((s) => (
                <div
                  key={s.id}
                  onClick={() => setSelectedSchool(s)}
                  className={`p-3 cursor-pointer hover:bg-slate-50 ${
                    selectedSchool?.id === s.id
                      ? "bg-blue-50 font-medium"
                      : ""
                  }`}
                >
                  {s.name}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              New school
            </label>

            <input
              className="border p-2 w-full mb-2"
              value={newSchoolName}
              onChange={(e) =>
                setNewSchoolName(e.target.value)
              }
            />

            <button
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={handleCreateSchool}
            >
              Add school
            </button>
          </div>
        </div>

        {/* RIGHT: DETAILS */}
        <div className="space-y-6">
          {!selectedSchool ? (
            <div className="border rounded p-4 bg-white">
              Select a school
            </div>
          ) : (
            <>
              {/* EDIT SCHOOL */}
              <div className="border rounded p-4 bg-white space-y-4">
                <h2 className="text-lg font-semibold">
                  Edit school
                </h2>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Name
                  </label>

                  <div className="flex gap-2">
                    <input
                      className="border p-2 w-full max-w-xs"
                      value={selectedSchool.name}
                      onChange={(e) =>
                        setSelectedSchool({
                          ...selectedSchool,
                          name: e.target.value,
                        })
                      }
                    />

                    <button
                      className="bg-blue-600 text-white px-4 py-2 rounded"
                      onClick={handleUpdateSchool}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>

              {/* DOMAINS */}
              <div className="border rounded p-4 bg-white space-y-4">
                <h2 className="text-lg font-semibold">
                  Domains
                </h2>

                <div className="border rounded divide-y">
                  {selectedSchool.domains.map((d) => (
                    <div
                      key={d.id}
                      className="p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium">
                          {d.domain}
                        </div>

                        <div className="text-xs text-slate-500">
                          {d.is_primary ? "Primary" : ""}
                          {!d.is_active ? " • Inactive" : ""}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          className="bg-slate-200 px-2 py-1 rounded text-sm"
                          onClick={() => togglePrimary(d)}
                        >
                          Primary
                        </button>

                        <button
                          className="bg-slate-200 px-2 py-1 rounded text-sm"
                          onClick={() => toggleActive(d)}
                        >
                          Active
                        </button>

                        <button
                          className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm"
                          onClick={() =>
                            handleDeleteDomain(d.id)
                          }
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Add domain
                  </label>

                  <div className="flex gap-2">
                    <input
                      className="border p-2 w-full max-w-xs"
                      value={newDomain}
                      onChange={(e) =>
                        setNewDomain(e.target.value)
                      }
                      placeholder="school.co.uk"
                    />

                    <button
                      className="bg-blue-600 text-white px-4 py-2 rounded"
                      onClick={handleAddDomain}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* ARBOR INTEGRATION */}
              <div className="border rounded p-4 bg-white space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">
                    Arbor Integration
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Configure this school's Arbor API connection.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Arbor base URL
                  </label>

                  <input
                    type="url"
                    className="border p-2 w-full"
                    value={arborBaseUrl}
                    onChange={(e) =>
                      setArborBaseUrl(e.target.value)
                    }
                    placeholder="https://school.uk.arbor.sc"
                  />

                  <p className="text-xs text-slate-500 mt-1">
                    Enter the Arbor school URL without /rest-v2
                    or /graphql/query.
                  </p>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={arborEnabled}
                    onChange={(e) =>
                      setArborEnabled(e.target.checked)
                    }
                  />

                  <span className="text-sm font-medium">
                    Enabled
                  </span>
                </label>

                {arborMessage && (
                  <div className="text-sm text-slate-600">
                    {arborMessage}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
                    onClick={handleSaveArborIntegration}
                    disabled={
                      arborSaving || !arborBaseUrl.trim()
                    }
                  >
                    {arborSaving
                      ? "Saving..."
                      : selectedSchool.arbor_integration
                      ? "Save Arbor Integration"
                      : "Add Arbor Integration"}
                  </button>

                  {selectedSchool.arbor_integration && (
                    <button
                      className="bg-red-100 text-red-700 px-4 py-2 rounded disabled:opacity-50"
                      onClick={handleDeleteArborIntegration}
                      disabled={arborSaving}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}